import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

const SHARED_CLIENTS_STORAGE_KEY = 'quoting-shared-clients';

// Only these fields are shared between the Quotation and Auto Quotation pages.
// Anything else a page keeps on a client (server ids, page specific extras) stays
// on that page.
const SHARED_CLIENT_FIELDS = [
  'full_name',
  'age',
  'gender',
  'nationality',
  'country_of_residence',
  'relationship',
  'area_of_coverage',
  'currency',
  'payment_frequency'
];

function normalizeSharedClient(client) {
  const normalized = { uid: client?.uid || uuidv4() };

  SHARED_CLIENT_FIELDS.forEach(field => {
    const value = client?.[field];

    normalized[field] = value === null || value === undefined ? '' : value;
  });

  return normalized;
}

// A client with every shared field blank, for starting a new quotation.
export function createEmptySharedClient() {
  return normalizeSharedClient({});
}

export function loadSharedClients() {
  try {
    const stored = localStorage.getItem(SHARED_CLIENTS_STORAGE_KEY);

    if (!stored) return null;

    const parsed = JSON.parse(stored);

    if (!Array.isArray(parsed) || parsed.length === 0) return null;

    return parsed.map(normalizeSharedClient);
  } catch (error) {
    console.error('Failed to read shared clients from localStorage:', error);
    return null;
  }
}

export function saveSharedClients(clients) {
  try {
    if (!Array.isArray(clients) || clients.length === 0) {
      localStorage.removeItem(SHARED_CLIENTS_STORAGE_KEY);
      return;
    }

    localStorage.setItem(SHARED_CLIENTS_STORAGE_KEY, JSON.stringify(clients.map(normalizeSharedClient)));
  } catch (error) {
    console.error('Failed to persist shared clients to localStorage:', error);
  }
}

export function clearSharedClients() {
  try {
    localStorage.removeItem(SHARED_CLIENTS_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear shared clients from localStorage:', error);
  }
}

/**
 * Clients state that is shared between pages through localStorage, so navigating
 * between the Quotation and Auto Quotation pages keeps the same client rows.
 *
 * @param createInitialClients called only when there is nothing stored yet.
 * @param inputTableRef ref of the InputTable rendering these clients. Full Name and
 *        Age are uncontrolled inputs there, so the DOM - not `clients` - holds their
 *        latest values; the ref is used to read them back before persisting.
 *
 * @returns [clients, setClients, handleClientsInput]
 */
export function useSharedClients(createInitialClients, inputTableRef) {
  const [clients, setClients] = useState(() => loadSharedClients() || createInitialClients());

  const persistClients = useCallback(fallbackClients => {
    const clientsFromInputTable = inputTableRef?.current?.readClients?.();

    const clientsToPersist = Array.isArray(clientsFromInputTable) && clientsFromInputTable.length === fallbackClients.length
      ? clientsFromInputTable
      : fallbackClients;

    saveSharedClients(clientsToPersist);
  }, [inputTableRef]);

  useEffect(() => {
    persistClients(clients);
  }, [clients, persistClients]);

  // Typing in Full Name / Age never reaches `clients` state, so InputTable calls this
  // on every keystroke to keep the shared copy up to date.
  const handleClientsInput = useCallback(clientsFromInputTable => {
    saveSharedClients(clientsFromInputTable);
  }, []);

  return [clients, setClients, handleClientsInput];
}
